import type {Dispatch, ReactElement, SetStateAction} from 'react'
import {useRef, useState} from 'react'
import {useOutletContext, useRouteLoaderData} from '@remix-run/react'
import {useInView} from 'react-intersection-observer'
import clsx from 'clsx'
import type {InViewRefCardProps, RelatedList, RootAndListContext, RootLoaderResponse} from '~/types/index.js'
import {getTranslation} from '~/localization/translation'
import {BrgIconArrowBack} from '~/icons/brg-icon-arrow-back'
import {BrgIconArrowForward} from '~/icons/brg-icon-arrow-forward'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {Vector} from '~/icons/vector-bottom-recommended-widget.js'
import {getDomainConfig} from '~/domains/index.js'
import {RelatedListCard} from './recommended-product-card.js'

export interface RecommendedProductsProps {
  lists: RelatedList[]
}

const CARDS_DATA_TEST_ID = 'related-list-card'
const CARDS_GAP_PIXELS = 16

export function RecommendedProducts() {
  const {language, domain, forcedLanguage, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse: {relatedLists: lists},
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const [refItems, setRefItems] = useState<InViewRefCardProps>({
    firstItemInView: true,
    lastItemInView: false,
  })
  const reportArrowClick = useReportWidgetClick()

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'related-lists-list',
    widget_place: 'under-10th-product',
  })

  const carouselRef = useRef<HTMLDivElement>(null)
  const firstCardElementRef = useRef<HTMLDivElement>(null)

  const handleScroll = (direction: 'back' | 'forward') => {
    const scrollLeft = carouselRef.current?.scrollLeft ?? 0
    const cardWidth = firstCardElementRef.current?.getBoundingClientRect().width ?? 0
    if (cardWidth) {
      carouselRef.current?.scroll({
        left:
          direction === 'back' ? scrollLeft - cardWidth - CARDS_GAP_PIXELS : cardWidth + CARDS_GAP_PIXELS + scrollLeft,
        behavior: 'smooth',
      })
      reportArrowClick({
        widget_name: direction === 'back' ? 'arrow-left-scroll' : 'arrow-right-scroll',
        widget_place: 'related-lists-list',
      })
    }
  }

  return lists && !!lists.length ? (
    <div
      ref={widgetRef}
      data-testid="recommended-products"
      id="recommended-products"
      className={clsx(
        'relative isolate flex min-w-full flex-col overflow-x-hidden rounded-3xl pb-7 pl-8 pt-4',
        stylesBydomain.LISTPAGE_WIDGETS_RECOMMENDED_PRODUCTS,
        'listpage_widgets_recommended_products',
      )}
    >
      <Vector className="vector-bottom absolute bottom-0 left-0" />
      <Vector className="vector-top absolute right-0 top-0 rotate-180 mobile:hidden" />
      <div className="mb-6 mt-5 flex justify-between">
        <p className="mb-1 text-2xl font-bold leading-8">{translations.RecommendedLists.title}</p>
        <div className="top-1 z-10 flex flex-row gap-2 pr-8 mobile:hidden">
          <button
            aria-label="show previous items"
            disabled={refItems.firstItemInView}
            data-testid="previous-button"
            type="button"
            onClick={() => handleScroll('back')}
            className={clsx(
              'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
              `${stylesBydomain.LISTPAGE_BUTTON_ARROW_BG} listpage_button_arrow_bg`,
              `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_FILL} listpage_button_arrow_icon_fill`,
              `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_STROKE} listpage_button_arrow_icon_stroke`,
            )}
          >
            <BrgIconArrowBack />
          </button>
          <button
            aria-label="show next items"
            type="button"
            data-testid="next-button"
            disabled={refItems.lastItemInView}
            onClick={() => handleScroll('forward')}
            className={clsx(
              'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
              `${stylesBydomain.LISTPAGE_BUTTON_ARROW_BG} listpage_button_arrow_bg`,
              `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_FILL} listpage_button_arrow_icon_fill`,
              `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_STROKE} listpage_button_arrow_icon_stroke`,
            )}
          >
            <BrgIconArrowForward />
          </button>
        </div>
      </div>
      <div
        id="carousel"
        className="no-scrollbar flex gap-4 overflow-x-auto"
        ref={carouselRef}
        data-testid="recommended-products-container"
      >
        {lists?.map((list, index: number) => {
          const listUrl = forcedLanguage ? `/${forcedLanguage}${list.url}` : list.url
          return (
            <InViewComponent
              key={`${list.url}-${index}}`}
              setRefItems={setRefItems}
              itemIndex={index === 0 ? 0 : index === lists.length - 1 ? 1 : undefined}
            >
              <RelatedListCard
                ref={index === 0 ? firstCardElementRef : null}
                index={index}
                name={list.name}
                url={listUrl}
                image={list.image}
                dataTestId={CARDS_DATA_TEST_ID}
              />
            </InViewComponent>
          )
        })}
      </div>
    </div>
  ) : null
}

function InViewComponent({
  itemIndex,
  setRefItems,
  children,
}: {
  itemIndex: number | undefined
  setRefItems: Dispatch<SetStateAction<InViewRefCardProps>>
  children: ReactElement
}) {
  const {ref} = useInView({
    threshold: 0.9,
    onChange: (inView) => {
      if (itemIndex === 0) {
        setRefItems((prev) => ({...prev, firstItemInView: inView}))
      }
      if (itemIndex === 1) setRefItems((prev) => ({...prev, lastItemInView: inView}))
    },
  })

  return <div ref={ref}>{children}</div>
}
